import { useKontentHomeConfig } from '@/components/contexts/KontentHomeConfigProvider'
import { CourseStatus } from '@/components/prescriptions/utils/prescriptionSelectionRules'
import { useIframeSide } from '@/hooks/useIframe'
import { useSideBySideSupport } from '@/hooks/useSideBySideSupport'
import type { PrescriptionSelectionCourse } from '@/types/fetchPrescriptionSelection'
import { Icon } from '@iconify/react'
import { forwardRef, useEffect, useId, useRef, type ReactNode } from 'react'
import sanitizeHtml from 'sanitize-html'
import {
	openSideBySideWithUrl,
	type OpenSideBySideContext,
} from './utils/openSideBySide'

export interface PrescriptionCourseAccordionProps {
	course: PrescriptionSelectionCourse
	status: CourseStatus
	isOpen: boolean
	onToggle: () => void
	children: ReactNode
}

const STATUS_LABEL: Record<CourseStatus, string> = {
	optional: 'optional',
	incomplete: 'incomplete',
	complete: 'complete',
}

const SANITIZE_OPTIONS: sanitizeHtml.IOptions = {
	allowedTags: [
		'p',
		'ul',
		'ol',
		'li',
		'strong',
		'em',
		'b',
		'i',
		'a',
		'br',
		'span',
	],
	allowedAttributes: {
		// `data-item-enable-compare-view` drives the side-by-side control (§ below)
		// and must survive sanitisation.
		a: ['href', 'target', 'rel', 'data-item-enable-compare-view'],
	},
}

const COMPARE_VIEW_ICON_SVG =
	'<svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">' +
	'<path d="M4 20C3.45 20 2.97917 19.8042 2.5875 19.4125C2.19583 19.0208 2 18.55 2 18V6C2 5.45 2.19583 4.97917 2.5875 4.5875C2.97917 4.19583 3.45 4 4 4H9V6H4V18H9V20H4ZM11 22V2H13V4H20C20.55 4 21.0208 4.19583 21.4125 4.5875C21.8042 4.97917 22 5.45 22 6V18C22 18.55 21.8042 19.0208 21.4125 19.4125C21.0208 19.8042 20.55 20 20 20H13V22H11ZM13 18H20V6H13V18Z" fill="currentColor"/>' +
	'</svg>'

const toCurrentOriginPath = (href: string): string => {
	try {
		const url = new URL(href, window.location.origin)
		return `${url.pathname}${url.search}${url.hash}`
	} catch {
		return href
	}
}

// Build the divider + split-view icon button inserted after a compare-view link.
const createCompareViewControl = (
	href: string,
	ctx: OpenSideBySideContext,
): { element: HTMLElement; cleanup: () => void } => {
	const wrapper = document.createElement('span')
	wrapper.className =
		'prescription-compare-view inline-flex ml-[10px] align-sub'

	const divider = document.createElement('div')
	divider.className = 'w-[1px] h-[24px] bg-gray-400 box-content'

	const button = document.createElement('button')
	button.type = 'button'
	button.setAttribute('aria-label', 'Open in View side-by-side')
	button.className = 'inline-block ml-1 text-nsw-brand-dark w-8'
	button.innerHTML = COMPARE_VIEW_ICON_SVG

	const onClick = (event: MouseEvent) => {
		event.preventDefault()
		openSideBySideWithUrl(toCurrentOriginPath(href), ctx)
	}
	// Focus ring matches the reference controls (ContentOverarchingLinks /
	// RichtextLinkWeblinkCommon).
	const onFocus = () => {
		button.style.outline = 'none'
		button.style.boxShadow = '0 0 0 3px var(--nsw-focus)'
	}
	const onBlur = () => {
		button.style.boxShadow = 'none'
	}
	button.addEventListener('click', onClick)
	button.addEventListener('focus', onFocus)
	button.addEventListener('blur', onBlur)

	wrapper.appendChild(divider)
	wrapper.appendChild(button)

	return {
		element: wrapper,
		cleanup: () => {
			button.removeEventListener('click', onClick)
			button.removeEventListener('focus', onFocus)
			button.removeEventListener('blur', onBlur)
			wrapper.remove()
		},
	}
}

const isExternalUrl = (url: string): boolean => {
	if (!/^https?:\/\//i.test(url)) return false
	if (typeof window === 'undefined') return true
	try {
		const parsed = new URL(url)
		return parsed.host !== window.location.host
	} catch {
		return false
	}
}

export const PrescriptionCourseAccordion = forwardRef<
	HTMLDivElement,
	PrescriptionCourseAccordionProps
>(function PrescriptionCourseAccordion(
	{ course, status, isOpen, onToggle, children },
	ref,
) {
	const reactId = useId()
	const headerId = `prescription-accordion-header-${reactId}`
	const panelId = `prescription-accordion-panel-${reactId}`
	const courseTitle = course['course-title']
	const requirementsSummary = course['requirements-summary'] ?? ''
	const quickLinks = course['quick-links'] ?? []
	const showQuickLins = false
	const sanitizedInfo = sanitizeHtml(requirementsSummary, SANITIZE_OPTIONS)
	const summaryRef = useRef<HTMLDivElement>(null)

	const isViewSideBySideSupported = useSideBySideSupport()
	const { isInIframe } = useKontentHomeConfig()
	const iframeSide = useIframeSide()

	useEffect(() => {
		if (!isOpen) return
		const root = summaryRef.current
		if (!root) return
		const anchors = root.querySelectorAll<HTMLAnchorElement>('a[href]')
		const cleanups: Array<() => void> = []
		anchors.forEach((a) => {
			const href = a.getAttribute('href') ?? ''
			if (!href || /^mailto:/i.test(href)) return
			try {
				const url = new URL(href, window.location.href)
				if (url.host && url.host !== window.location.host) {
					a.setAttribute('target', '_blank')
					a.setAttribute('rel', 'noopener noreferrer')
				}
			} catch {
				// Invalid URL — skip.
			}

			const enableCompareView =
				a.getAttribute('data-item-enable-compare-view') === 'true'
			if (!enableCompareView || !isViewSideBySideSupported) return
			const next = a.nextElementSibling
			if (next?.classList.contains('prescription-compare-view')) return

			const control = createCompareViewControl(href, {
				isInIframe,
				iframeSide,
			})
			a.insertAdjacentElement('afterend', control.element)
			cleanups.push(control.cleanup)
		})
		return () => cleanups.forEach((fn) => fn())
	}, [
		sanitizedInfo,
		isOpen,
		isViewSideBySideSupported,
		isInIframe,
		iframeSide,
	])

	return (
		<div
			ref={ref}
			className="nesa-accordion"
			data-testid="prescription-course-accordion"
			data-course-title={courseTitle}
		>
			<button
				id={headerId}
				type="button"
				aria-expanded={isOpen}
				aria-controls={panelId}
				onClick={onToggle}
				className={[
					'nesa-accordion__header',
					isOpen ? 'nesa-accordion__active' : '',
					'w-full flex items-center justify-between gap-3 text-left',
					'focus:outline focus:outline-3 focus:outline-[color:var(--nsw-focus)]',
				].join(' ')}
			>
				<span className="font-bold text-lg truncate flex-1 min-w-0">
					{courseTitle}
				</span>
				<span className="shrink-0 flex items-center gap-3">
					{status !== 'optional' && (
						<span
							className="text-sm"
							aria-label={`Status: ${STATUS_LABEL[status]}`}
							data-testid="prescription-course-status"
						>
							{STATUS_LABEL[status]}
						</span>
					)}
					<Icon
						icon={
							isOpen
								? 'ic:baseline-expand-less'
								: 'ic:baseline-expand-more'
						}
						width={28}
						height={28}
					/>
				</span>
			</button>

			{isOpen && (
				<div
					id={panelId}
					role="region"
					aria-labelledby={headerId}
					className="p-4 bg-white"
				>
					<div className="flex flex-col gap-6">
						<div className="text-nsw-grey-01">
							{sanitizedInfo && (
								// eslint-disable-next-line react/no-danger
								<div
									ref={summaryRef}
									className="requirements-summary richtext"
									dangerouslySetInnerHTML={{
										__html: sanitizedInfo,
									}}
								/>
							)}
							{quickLinks.length > 0 && showQuickLins && (
								<div className="mt-4">
									<p className="font-bold mb-2">
										Quick links
									</p>
									<ul className="list-disc pl-5">
										{quickLinks.map((link, idx) => {
											const external = isExternalUrl(
												link.url,
											)
											return (
												<li
													key={`${idx}-${link.url}`}
													className="mb-1"
												>
													<a
														href={link.url}
														className="nsw-text--brand-dark underline"
														{...(external
															? {
																	target: '_blank',
																	rel: 'noopener noreferrer',
															  }
															: {})}
													>
														{link.title}
														{external && (
															<Icon
																icon="octicon:link-external-16"
																width={14}
																height={14}
																className="inline-block ml-1 align-middle"
															/>
														)}
													</a>
												</li>
											)
										})}
									</ul>
								</div>
							)}
						</div>
						<div>{children}</div>
					</div>
				</div>
			)}
		</div>
	)
})

export default PrescriptionCourseAccordion
